/*
 * Behaviour for the Navy Toys sections. Plain custom elements, like Dawn's own, so a section
 * re-rendered by the theme editor wires itself up again without any global init.
 */

/**
 * Tabs that switch on hover as well as click, as on the old storefront.
 * Markup: buttons [role=tab][aria-controls] and panels [role=tabpanel] inside the element.
 */
class NavyTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab));
      tab.addEventListener('focus', () => this.select(tab));
      // Hover only where a real pointer exists; on touch the first tap would count as hover.
      tab.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.select(tab);
      });
    });
    this.addEventListener('keydown', (event) => this.onKeydown(event));
  }

  select(tab) {
    this.tabs.forEach((candidate) => {
      const active = candidate === tab;
      candidate.setAttribute('aria-selected', String(active));
      candidate.tabIndex = active ? 0 : -1;
      const panel = this.querySelector(`#${candidate.getAttribute('aria-controls')}`);
      if (panel) panel.hidden = !active;
    });
  }

  onKeydown(event) {
    const index = this.tabs.indexOf(document.activeElement);
    if (index === -1) return;
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    this.tabs[(index + step + this.tabs.length) % this.tabs.length].focus();
  }
}

if (!customElements.get('navy-tabs')) customElements.define('navy-tabs', NavyTabs);

/** A horizontal move at least this long, and longer than the vertical one, counts as a swipe. */
const SWIPE_MIN_DISTANCE = 40;

/**
 * Hero slides: cross-fading (navy.css), dots, swipe on touch screens, and optional autoplay
 * (section settings).
 * Autoplay pauses while the pointer or keyboard focus is on the slides, so a visitor can read
 * and click; it never runs in the theme editor (slides would jump while being edited) or for
 * visitors who ask for reduced motion.
 */
class NavySlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-slide-dot]'));
    this.current = 0;
    this.pausedBy = new Set();
    this.dots.forEach((dot, index) =>
      dot.addEventListener('click', () => {
        this.show(index);
        this.restartAutoplay();
      })
    );
    if (this.slides.length < 2) return;

    // Switch from `hidden` (no-JS fallback) to the cross-fade classes in navy.css.
    this.slides.forEach((slide) => slide.removeAttribute('hidden'));
    this.show(0);
    this.classList.add('is-ready');

    this.listenForSwipes();
    // In the theme editor, show the slide the merchant picks in the sidebar.
    this.onBlockSelect = (event) => {
      const index = this.slides.indexOf(event.target);
      if (index !== -1) this.show(index);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const inThemeEditor = Boolean(window.Shopify && window.Shopify.designMode);
    this.autoplayDelay = Number(this.dataset.autoplaySeconds) * 1000;
    if (this.dataset.autoplay !== 'true' || !(this.autoplayDelay > 0) || reducedMotion || inThemeEditor) {
      this.autoplayDelay = 0;
      return;
    }

    // Real mouse and keyboard focus only: a tap on a phone also fires mouse and focus events
    // with no matching "leave", which would stop autoplay for good after the first tap.
    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.pause('hover');
    });
    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') this.resume('hover');
    });
    this.addEventListener('focusin', (event) => {
      if (event.target.matches(':focus-visible')) this.pause('focus');
    });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.resume('focus');
    });
    // A background tab would otherwise keep advancing and show a random slide on return.
    this.onVisibilityChange = () => (document.hidden ? this.pause('hidden') : this.resume('hidden'));
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.restartAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  show(index) {
    this.current = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.current;
      slide.classList.toggle('is-active', active);
      // Faded-out slides keep their links out of the tab order and screen readers.
      slide.inert = !active;
    });
    this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === this.current)));
  }

  listenForSwipes() {
    let start = null;
    this.addEventListener(
      'touchstart',
      (event) => {
        start = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      },
      { passive: true }
    );
    this.addEventListener(
      'touchend',
      (event) => {
        if (!start) return;
        const deltaX = event.changedTouches[0].clientX - start.x;
        const deltaY = event.changedTouches[0].clientY - start.y;
        start = null;
        // Mostly vertical moves are page scrolls, not swipes.
        if (Math.abs(deltaX) < SWIPE_MIN_DISTANCE || Math.abs(deltaX) < Math.abs(deltaY)) return;
        this.show(this.current + (deltaX < 0 ? 1 : -1));
        this.restartAutoplay();
      },
      { passive: true }
    );
  }

  pause(reason) {
    this.pausedBy.add(reason);
    this.stopAutoplay();
  }

  resume(reason) {
    this.pausedBy.delete(reason);
    this.restartAutoplay();
  }

  restartAutoplay() {
    this.stopAutoplay();
    if (!this.autoplayDelay || this.pausedBy.size > 0) return;
    this.timer = window.setTimeout(() => {
      this.show(this.current + 1);
      this.restartAutoplay();
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    window.clearTimeout(this.timer);
    this.timer = undefined;
  }
}

if (!customElements.get('navy-slideshow')) customElements.define('navy-slideshow', NavySlideshow);

/** Lowercase without accents, so "pokemon" finds "Pokémon". */
function searchKey(text) {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/**
 * Collection sidebar (snippets/navy-filter-tree.liquid): "Show more" per list, and a search box
 * that narrows every list to matching entries, opening the categories that hold a match.
 */
class NavyFilterPanel extends HTMLElement {
  connectedCallback() {
    this.groups = Array.from(this.querySelectorAll('[data-filter-group]'));
    this.moreGroups = this.querySelector('[data-filter-more-groups]');
    this.empty = this.querySelector('[data-filter-empty]');
    this.querySelectorAll('[data-filter-more]').forEach((button) =>
      button.addEventListener('click', () => this.toggleMore(button))
    );
    const input = this.querySelector('[data-filter-search]');
    // Remember which categories were open, to put them back when the search is cleared.
    this.openBefore = new Map(
      [...this.groups, this.moreGroups].filter(Boolean).map((group) => [group, group.querySelector('details')?.open ?? false])
    );
    // The box sits inside Dawn's filter form, which re-requests the page on every input event.
    input?.addEventListener('input', (event) => {
      event.stopPropagation();
      this.search(searchKey(input.value));
    });
    input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') event.preventDefault();
    });
  }

  toggleMore(button) {
    const group = button.closest('[data-filter-group]');
    const expanded = group.classList.toggle('is-expanded');
    button.setAttribute('aria-expanded', String(expanded));
    button.querySelector('[data-more-label]').hidden = expanded;
    button.querySelector('[data-less-label]').hidden = !expanded;
  }

  search(query) {
    this.classList.toggle('is-searching', query !== '');
    let anyVisible = false;
    this.groups.forEach((group) => {
      const details = group.querySelector('details');
      const title = details?.querySelector('summary [data-filter-text]');
      const titleMatches = Boolean(title) && searchKey(title.textContent).includes(query);
      let itemMatches = false;
      group.querySelectorAll('[data-filter-item]').forEach((item) => {
        const matches = query === '' || titleMatches || searchKey(item.textContent).includes(query);
        item.hidden = !matches;
        if (matches && query !== '') itemMatches = true;
      });
      const visible = query === '' || titleMatches || itemMatches;
      group.hidden = !visible;
      if (visible) anyVisible = true;
      if (details) details.open = query === '' ? this.openBefore.get(group) : itemMatches;
    });
    if (this.moreGroups) {
      // "More categories" opens for a match inside it and hides when nothing in it matches.
      const inner = Array.from(this.moreGroups.querySelectorAll('[data-filter-group]'));
      const innerVisible = inner.some((group) => !group.hidden);
      this.moreGroups.hidden = !innerVisible;
      this.moreGroups.querySelector('details').open = query === '' ? this.openBefore.get(this.moreGroups) : innerVisible;
    }
    if (this.empty) this.empty.hidden = anyVisible;
  }
}

if (!customElements.get('navy-filter-panel')) customElements.define('navy-filter-panel', NavyFilterPanel);

/**
 * Search box above a long Shopify filter list (snippets/navy-facet-search.liquid). Matches show
 * even past Dawn's "Show more" cut; the box lives inside the filter form, so its input events
 * must not reach facets.js, which would re-request the page.
 */
class NavyFacetSearch extends HTMLElement {
  connectedCallback() {
    this.list = this.closest('.facets__display-vertical, .mobile-facets__submenu');
    const input = this.querySelector('[data-facet-search]');
    if (!this.list || !input) return;
    input.addEventListener('input', (event) => {
      event.stopPropagation();
      this.search(searchKey(input.value));
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') event.preventDefault();
    });
  }

  search(query) {
    this.list.classList.toggle('navy-facet-searching', query !== '');
    this.list.querySelectorAll('.facets__item, .mobile-facets__item').forEach((item) => {
      item.toggleAttribute('data-facet-miss', query !== '' && !searchKey(item.textContent).includes(query));
    });
  }
}

if (!customElements.get('navy-facet-search')) customElements.define('navy-facet-search', NavyFacetSearch);

/** Wait this long after the last keystroke before asking Shopify, so typing a word costs one request. */
const COLLECTION_SEARCH_DELAY_MS = 200;
const COLLECTION_SEARCH_LIMIT = 10;

/**
 * Collections page search (snippets/navy-collection-search.liquid): matches from every
 * collection via Shopify's predictive search, shown as a list of links with a thumbnail.
 */
class NavyCollectionSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-collection-search]');
    this.results = this.querySelector('.navy-collection-search__results');
    this.empty = this.querySelector('.navy-collection-search__empty');
    if (!this.input || !this.results) return;
    this.input.addEventListener('input', () => {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.search(this.input.value.trim()), COLLECTION_SEARCH_DELAY_MS);
    });
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  async search(query) {
    this.controller?.abort();
    if (query.length < 2) {
      this.close();
      return;
    }
    this.controller = new AbortController();
    const root = window.routes?.predictive_search_url ?? '/search/suggest';
    const params = new URLSearchParams({ q: query, 'resources[type]': 'collection', 'resources[limit]': String(COLLECTION_SEARCH_LIMIT) });
    try {
      const response = await fetch(`${root}.json?${params}`, { signal: this.controller.signal });
      if (!response.ok) return;
      const data = await response.json();
      this.render(data.resources?.results?.collections ?? []);
    } catch (error) {
      // AbortError: a newer keystroke replaced this request. Otherwise (offline, blocked) the
      // form still works: Enter falls back to the search page.
      if (error.name !== 'AbortError') this.close();
    }
  }

  render(collections) {
    this.results.replaceChildren(
      ...collections.map((collection, index) => {
        const item = document.createElement('li');
        item.setAttribute('role', 'option');
        item.id = `NavyCollectionResult-${index}`;
        const link = document.createElement('a');
        link.className = 'navy-collection-search__link';
        link.href = collection.url;
        if (collection.featured_image?.url) {
          const image = document.createElement('img');
          const url = collection.featured_image.url;
          image.src = `${url}${url.includes('?') ? '&' : '?'}width=96`;
          image.alt = '';
          image.width = 48;
          image.height = 48;
          image.loading = 'lazy';
          link.append(image);
        }
        const title = document.createElement('span');
        title.textContent = collection.title;
        link.append(title);
        item.append(link);
        return item;
      })
    );
    this.active = -1;
    this.results.hidden = collections.length === 0;
    if (this.empty) this.empty.hidden = collections.length > 0;
    this.input.setAttribute('aria-expanded', String(collections.length > 0));
  }

  close() {
    this.results.hidden = true;
    if (this.empty) this.empty.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }

  onKeydown(event) {
    const options = Array.from(this.results.querySelectorAll('[role="option"]'));
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (this.results.hidden || options.length === 0) return;
    const step = { ArrowDown: 1, ArrowUp: -1 }[event.key];
    if (step) {
      event.preventDefault();
      this.active = (this.active + step + options.length) % options.length;
      options.forEach((option, index) => option.classList.toggle('is-active', index === this.active));
      this.input.setAttribute('aria-activedescendant', options[this.active].id);
    } else if (event.key === 'Enter' && this.active >= 0) {
      event.preventDefault();
      window.location.href = options[this.active].querySelector('a').href;
    }
  }
}

if (!customElements.get('navy-collection-search')) customElements.define('navy-collection-search', NavyCollectionSearch);

/*
 * Sticky sidebars that scroll inside (navy.css): mark them while their content is cut off, scrolled
 * down and scrolled to the end, so the cut edges can fade.
 */
(function markClippedSidebars() {
  const sidebars = document.querySelectorAll('.facets-vertical .facets-wrapper, .navy-collections-layout__sidebar');
  if (!sidebars.length) return;
  const update = (sidebar) => {
    const clipped = sidebar.scrollHeight > sidebar.clientHeight + 1;
    sidebar.classList.toggle('is-clipped', clipped);
    sidebar.classList.toggle('is-scrolled', clipped && sidebar.scrollTop > 1);
    sidebar.classList.toggle('is-at-end', clipped && sidebar.scrollTop + sidebar.clientHeight >= sidebar.scrollHeight - 1);
  };
  const observer = new ResizeObserver((entries) => entries.forEach((entry) => update(entry.target.closest('.facets-wrapper, .navy-collections-layout__sidebar'))));
  sidebars.forEach((sidebar) => {
    sidebar.addEventListener('scroll', () => update(sidebar), { passive: true });
    // facets.js swaps the filter markup after each change, so observed children can go stale;
    // opening a category or "Show more" also comes through here.
    sidebar.addEventListener('toggle', () => update(sidebar), true);
    sidebar.addEventListener('click', () => requestAnimationFrame(() => update(sidebar)));
    // Opening a category or "Show more" changes the content height, not the sidebar's own box.
    observer.observe(sidebar);
    Array.from(sidebar.children).forEach((child) => observer.observe(child));
    update(sidebar);
  });
})();

/*
 * The sticky header shrinks once the page scrolls ("reduce logo size"), so its height at load
 * (Dawn's --header-height) is too tall for things pinned below it. Publish where it really ends.
 */
(function trackHeaderBottom() {
  const header = document.querySelector('.section-header');
  if (!header) return;
  let queued = false;
  const update = () => {
    queued = false;
    const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    document.documentElement.style.setProperty('--navy-header-bottom', `${bottom}px`);
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  // The header animates its height after a scroll; catch the size it settles on.
  new ResizeObserver(schedule).observe(header);
  update();
})();

/*
 * Dawn's horizontal filter dropdowns (<details class="facets__disclosure">) and the header's
 * currency and language lists only close when their own toggle is clicked again. Close them on a click
 * elsewhere or Escape, like a menu. Delegated on document, so filters re-rendered by
 * facets.js after each change still work.
 */
const DROPDOWNS = ['details.facets__disclosure', 'details.navy-currency__details', 'details.navy-language__details'];
const DROPDOWN = DROPDOWNS.join(', ');
const OPEN_DROPDOWN = DROPDOWNS.map((selector) => `${selector}[open]`).join(', ');

function closeOpenDropdowns(except) {
  document.querySelectorAll(OPEN_DROPDOWN).forEach((details) => {
    if (details !== except) details.removeAttribute('open');
  });
}

document.addEventListener('click', (event) => {
  closeOpenDropdowns(event.target.closest(DROPDOWN));
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const open = document.querySelector(OPEN_DROPDOWN);
  if (!open) return;
  closeOpenDropdowns();
  open.querySelector('summary')?.focus();
});

/*
 * Language switch without the localization form's round trip: the form posts to Shopify, which
 * answers with a redirect to the same page under the language's folder (/fr/…), costing about a
 * second before the new page even starts loading. Going to that folder directly is the same
 * result. Without JavaScript the buttons still submit the form.
 */
document.addEventListener('click', (event) => {
  const option = event.target.closest('.navy-language__option[data-root]');
  if (!option) return;
  const form = option.closest('form');
  const currentRoot = (form?.dataset.currentRoot || '/').replace(/\/$/, '');
  const targetRoot = option.dataset.root.replace(/\/$/, '');
  const path = currentRoot && location.pathname.startsWith(`${currentRoot}/`) ? location.pathname.slice(currentRoot.length) : location.pathname === currentRoot ? '/' : location.pathname;
  event.preventDefault();
  location.assign(`${targetRoot}${path === '/' && targetRoot ? '' : path}${location.search}${location.hash}`);
});

/*
 * Wishlist kept in this browser's localStorage, as on the old storefront: no account needed,
 * nothing synced between devices. Components listen for one event so every heart, the header
 * count and the wishlist page stay in step, across tabs too.
 */
const WISHLIST_KEY = 'navy-wishlist';
const WISHLIST_EVENT = 'navy:wishlist-change';

const navyWishlist = {
  read() {
    try {
      const value = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(value) ? value.filter((handle) => typeof handle === 'string') : [];
    } catch {
      // Private windows and blocked storage: behave as an empty list rather than break the page.
      return [];
    }
  },
  write(handles) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles));
    } catch {
      // Storage unavailable; the hearts still toggle for this page view.
    }
    document.dispatchEvent(new CustomEvent(WISHLIST_EVENT));
  },
  has(handle) {
    return this.read().includes(handle);
  },
  toggle(handle) {
    const handles = this.read();
    this.write(handles.includes(handle) ? handles.filter((saved) => saved !== handle) : [handle, ...handles]);
  },
};

window.addEventListener('storage', (event) => {
  if (event.key === WISHLIST_KEY) document.dispatchEvent(new CustomEvent(WISHLIST_EVENT));
});

/** Base for components that redraw whenever the wishlist changes. */
class NavyWishlistElement extends HTMLElement {
  connectedCallback() {
    this.onWishlistChange = () => this.render();
    document.addEventListener(WISHLIST_EVENT, this.onWishlistChange);
    this.setup?.();
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_EVENT, this.onWishlistChange);
  }
}

class NavyWishlistButton extends NavyWishlistElement {
  setup() {
    this.button = this.querySelector('button');
    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      navyWishlist.toggle(this.dataset.handle);
    });
  }

  render() {
    this.button.setAttribute('aria-pressed', String(navyWishlist.has(this.dataset.handle)));
  }
}

class NavyWishlistCount extends NavyWishlistElement {
  render() {
    const count = navyWishlist.read().length;
    this.textContent = count > 99 ? '99+' : String(count);
    this.hidden = count === 0;
  }
}

/** The wishlist page: fetches each saved product's card once, then hides removed ones. */
class NavyWishlistGrid extends NavyWishlistElement {
  setup() {
    this.list = this.querySelector('ul');
    this.empty = this.querySelector('.navy-wishlist__empty');
    this.load();
  }

  async load() {
    const handles = navyWishlist.read();
    const cards = await Promise.all(handles.map((handle) => this.fetchCard(handle)));
    cards.forEach((card, index) => {
      if (!card) return;
      const item = document.createElement('li');
      item.dataset.handle = handles[index];
      item.append(card);
      this.list.append(item);
    });
    this.loaded = true;
    this.render();
  }

  async fetchCard(handle) {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}products/${encodeURIComponent(handle)}?sections=navy-card-fragment`);
      if (!response.ok) return null; // the product was deleted or unpublished
      const html = (await response.json())['navy-card-fragment'];
      return new DOMParser().parseFromString(html, 'text/html').querySelector('.navy-card-wrap');
    } catch {
      return null;
    }
  }

  render() {
    if (!this.list) return;
    const saved = new Set(navyWishlist.read());
    this.list.querySelectorAll('li').forEach((item) => {
      item.hidden = !saved.has(item.dataset.handle);
    });
    // Until the cards arrive, trust the saved list; afterwards count what is actually shown,
    // so a list of deleted products still gets the empty message.
    const visible = this.loaded ? this.list.querySelectorAll('li:not([hidden])').length : saved.size;
    this.empty.hidden = visible > 0;
    const count = this.querySelector('.navy-wishlist__count');
    if (count) count.textContent = (visible === 1 ? count.dataset.one : count.dataset.other).replace('[count]', visible);
  }
}

/**
 * Buy bar fixed to the bottom of the product page on phones (snippets/navy-sticky-buy.liquid).
 * Shown while the real buy button is off screen; a tap presses that button, so the cart flow
 * stays the theme's own. Price, label and disabled state follow the real button and price.
 */
class NavyStickyBuy extends HTMLElement {
  connectedCallback() {
    this.submit = document.querySelector('product-info .product-form__submit') || document.querySelector('.product-form__submit');
    this.button = this.querySelector('[data-navy-sticky-button]');
    this.priceOutput = this.querySelector('[data-navy-sticky-price]');
    if (!this.submit || !this.button) {
      this.remove();
      return;
    }
    this.button.addEventListener('click', () => this.submit.click());

    this.observer = new IntersectionObserver(([entry]) => this.toggle(!entry.isIntersecting), { threshold: 0 });
    this.observer.observe(this.submit.closest('.product-form__buttons') || this.submit);

    this.sync();
    this.mutations = new MutationObserver(() => this.sync());
    this.mutations.observe(this.submit, { attributes: true, childList: true, subtree: true, characterData: true });
    const priceBox = document.querySelector('product-info [id^="price-"]') || document.querySelector('[id^="price-"]');
    if (priceBox) this.mutations.observe(priceBox, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.mutations?.disconnect();
    document.body.classList.remove('navy-sticky-buy-visible');
  }

  toggle(visible) {
    this.classList.toggle('is-visible', visible);
    document.body.classList.toggle('navy-sticky-buy-visible', visible);
  }

  sync() {
    const label = this.submit.textContent.trim().replace(/\s+/g, ' ');
    if (label) this.button.textContent = label;
    this.button.disabled = this.submit.disabled || this.submit.getAttribute('aria-disabled') === 'true';
    const priceBox = document.querySelector('product-info [id^="price-"]') || document.querySelector('[id^="price-"]');
    const price = priceBox?.querySelector('.price-item--sale, .price-item--regular');
    if (price && this.priceOutput) this.priceOutput.textContent = price.textContent.trim();
  }
}

if (!customElements.get('navy-sticky-buy')) customElements.define('navy-sticky-buy', NavyStickyBuy);
if (!customElements.get('navy-wishlist-button')) customElements.define('navy-wishlist-button', NavyWishlistButton);
if (!customElements.get('navy-wishlist-count')) customElements.define('navy-wishlist-count', NavyWishlistCount);
if (!customElements.get('navy-wishlist-grid')) customElements.define('navy-wishlist-grid', NavyWishlistGrid);
